import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/shared/lib/utils"

const buttonVariants = cva(
  // Sora-spec base: tight focus ring (1px solid accent-blue, no halo), no
  // box-shadow transition (Sora doctrine), tight transition only on color.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-ring aria-invalid:outline-destructive",
  {
    variants: {
      variant: {
        // Default: solid white-on-black, Sora's primary pattern.
        default: "bg-white text-black hover:bg-white/90",
        // Destructive: keep semantic red, simplified.
        destructive: "bg-destructive text-white hover:bg-destructive/90",
        // Outline: hairline + transparent + subtle hover wash. No shadow.
        outline: "border border-border bg-transparent hover:bg-foreground/[0.04] hover:text-foreground",
        // Secondary: subtle elevated wash, no solid fill.
        secondary: "bg-foreground/[0.06] text-foreground hover:bg-foreground/[0.10]",
        // Ghost: transparent until hover.
        ghost: "hover:bg-foreground/[0.04] hover:text-foreground",
        // Link: text-only, underline on hover.
        link: "text-foreground underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
